import PropTypes from "prop-types";
import React, { PureComponent, useMemo } from "react";
import { Grid } from "react-virtualized/dist/commonjs/Grid";
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer";
import itemViews from "components/items/views";
import Separator, { SortableSeparator } from "components/items/separator";
import Image, { SortableImage } from "components/items/image-separator";
import { style } from "glamor";
import Globalize from "globalize";
import DragPreview from "components/items/views/preview";
import { MAX_SELECT } from "constants/shift-select-types";
import { ITEM_SEPARATOR, ITEM, SCROLL_HEIGHT_MULTIPLIER } from "constants/catalogs";
import { createRecord as createItemRecord } from "records/item";
import _ from "lodash";
import { WindowScroller } from "react-virtualized/dist/commonjs/WindowScroller";
import { generateSelectedIdArray } from "../utils/utilities";
import { rawTheme } from "styles/theme";
import { getNodeKey } from "components/items/items-utils";
import { gtm } from "helpers/gtm";
import { getViewDef } from "helpers/view-def";

const EMPTY_CATALOG_DATES = {};

const SCROLL_WINDOW_EXTENSION = {
    linesheet: 667,
    "custom-list": 553,
};

const tombstoneRows = (grid, { columns = 0, View, fetchingRows = 0, resultLength = 0 }) => {
    if (!fetchingRows) {
        return;
    }

    // fill out remaining row
    while (grid[grid.length - 1] && grid[grid.length - 1].length < columns) {
        grid[grid.length - 1].push({
            width: View.width,
            height: View.height,
            index: resultLength,
        });
    }

    for (let i = 0; i < fetchingRows; i++) {
        grid.push([]);
        for (let j = 0; j < columns; j++) {
            grid[grid.length - 1].push({
                width: View.width,
                height: View.height,
                index: resultLength,
            });
        }
    }
};

const getGridForItems = ({ result, columns, entities, View, fetchingRows }) => {
    const grid = [];
    const { items = {} } = entities;
    for (let i = 0; i < result.length; i++) {
        const id = result[i];
        const item = items[id];

        if (grid.length === 0 || grid[grid.length - 1].length >= columns) {
            grid.push([]);
        }

        grid[grid.length - 1].push({
            item,
            width: View.width,
            height: View.height,
            index: i,
        });
    }

    tombstoneRows(grid, {
        columns,
        fetchingRows,
        View,
        resultLength: result.length,
    });

    return {
        grid,
        height: grid.length * (View.height + View.horizontalOffset),
    };
};

const calculateHeight = (type) => {
    let height;
    switch (type) {
        case ITEM_SEPARATOR:
            height = 54;
            break;
        case "image":
            height = 330;
            break;
        case ITEM:
        default:
            height = 300;
            break;
    }

    return height;
};

const getGridForCatalog = ({
    result,
    columns,
    entities,
    sortingIndex,
    width,
    View,
    editable,
    draggingEntryId,
    fetchingRows,
}) => {
    const grid = [];
    const { items = {}, entries = {}, schemas } = entities;
    let forceNewLine = false;

    const itemSchema = _.find(schemas, { type: "product" });

    let height = 0;
    for (let i = 0; i < result.length; i++) {
        const id = result[i];
        const entry = entries[id];
        if (!entry) {
            continue;
        }

        const ItemRec = createItemRecord(itemSchema);
        let item = items[entry.item || entry.item_id];
        if (item && entry && entry.__out_of_range !== item.__out_of_range) {
            // HACK to correct a mismatch between item knowing it is unavailable
            const itemData = item.toJS();
            itemData.__out_of_range = entry.__out_of_range;
            item = new ItemRec(itemData);
        }
        const isImageOrSeparator = entry.type === "image" || entry.type === ITEM_SEPARATOR;
        let spanAllColumns = entry && isImageOrSeparator ? true : false;
        if (spanAllColumns && (draggingEntryId === entry.id || entry.id.length > 24)) {
            spanAllColumns = false;
        }
        let lastRow = grid.length - 1;

        if (grid.length === 0 || spanAllColumns || forceNewLine || grid[lastRow].length >= columns) {
            // if editable add drobable zones
            if (
                editable &&
                grid.length !== 0 &&
                grid[lastRow].length < columns &&
                grid[lastRow][0].entry.type !== ITEM_SEPARATOR &&
                grid[lastRow][0].entry.type !== "image"
            ) {
                grid[lastRow].push({
                    width: width - grid[lastRow].length * View.width,
                    height: View.height,
                    index: i,
                    placeholder: true,
                });
            }
            grid.push([]);
            lastRow = grid.length - 1;
            forceNewLine = spanAllColumns;
            // height += spanAllColumns ? 54 : View.height + View.verticalOffset;
            if (spanAllColumns) {
                height += calculateHeight(entry.type);
            } else {
                height += View.height + View.verticalOffset;
            }
        }

        if (spanAllColumns && sortingIndex === i) {
            spanAllColumns = false;
            forceNewLine = false;
        }

        if (spanAllColumns) {
            grid[lastRow].push({
                entry,
                item,
                width: width,
                height: calculateHeight(entry.type),
                index: i,
            });
        } else {
            grid[lastRow].push({
                entry,
                item,
                width: View.width,
                height: View.height,
                index: i,
            });
        }
    }
    tombstoneRows(grid, {
        columns,
        fetchingRows,
        View,
        resultLength: result.length,
    });

    // add one to the end
    if (editable) {
        if (
            grid.length === 0 ||
            grid[grid.length - 1].length >= columns ||
            grid[grid.length - 1][0].entry.type === ITEM_SEPARATOR ||
            grid[grid.length - 1][0].entry.type === "image"
        ) {
            grid.push([]);
            height += View.height;
        }
        grid[grid.length - 1].push({
            width: width - grid[grid.length - 1].length * View.width,
            height: View.height,
            index: result.length,
            placeholder: true,
        });
    }

    return { grid, height };
};

const getViewBase = (view, editable) => {
    return typeof view === "string" ? itemViews[view][editable ? 1 : 0] : view;
};

class ItemsGrid extends PureComponent {
    static propTypes = {
        actions: PropTypes.object,
        activeBrand: PropTypes.object.isRequired,
        activeItemId: PropTypes.string,
        activeList: PropTypes.object,
        app: PropTypes.object.isRequired,
        cart: PropTypes.object.isRequired,
        editable: PropTypes.bool,
        entities: PropTypes.object,
        isActiveList: PropTypes.bool,
        onDrag: PropTypes.func,
        onDrop: PropTypes.func,
        pageSize: PropTypes.number.isRequired,
        results: PropTypes.array.isRequired,
        onSnackbarAdd: PropTypes.func.isRequired,
        view: PropTypes.oneOf(["tile", "largeTile", "panel", "card", "list", "assortment"]),
        viewNotes: PropTypes.bool,
        noContentRenderer: PropTypes.func,
        overrideStyles: PropTypes.object,
        scope: PropTypes.object.isRequired,
        onEntriesRefresh: PropTypes.func,
        bulkDragInsert: PropTypes.bool,
        bulkDragReorder: PropTypes.bool,
        toolbarMinimized: PropTypes.bool,
    };

    static defaultProps = {
        view: "panel",
        editable: false,
        isActiveList: false,
        entities: {},
    };

    styles = style({
        margin: "0 -5px",
    });

    constructor(props) {
        super(props);

        this.state = {
            viewDef: undefined,
            grid: [],
            gridHeight: 0,
            groupSelectStartValue: null,
            groupSelectEndValue: null,
            shiftKey: false,
        };

        this.cellRenderer = this.cellRenderer.bind(this);
        this.editableCellRenderer = this.editableCellRenderer.bind(this);
        this.computeGrid = this.computeGrid.bind(this);
        this.handleBulkItemSelectionChange = this.handleBulkItemSelectionChange.bind(this);
        this.handleWindowScrollEvent = this.handleWindowScrollEvent.bind(this);
    }

    UNSAFE_componentWillMount() {
        const { editable, view } = this.props;
        const View = getViewBase(view, editable);
        this.computeGrid(this.props, View);

        this.itemActions = {
            onItemClick: this.props.onItemClick,
            onBulkItemSelectionChange: this.handleBulkItemSelectionChange,
            onDelete: this.props.onDeleteEntry,
            onDeleteSelectedEntries: this.props.onDeleteSelectedEntries,
            onUpdate: this.props.onUpdateEntry,
            onViewNotes: this.props.onViewNotes,
            onActiveListAdd: this.props.onActiveListAdd,
            onActiveListRemove: this.props.onActiveListRemove,
            onActiveListFetchEntries: this.props.onActiveListFetchEntries,
            onSetActiveList: this.props.onSetActiveList,
            onMoreInfo: this.props.onItemMoreInfo,
        };

        if (!_.isEmpty(this.props.viewDef.result) && !_.isEmpty(this.props.results)) {
            this.collectCatalogItems();
        }

        this.loadViewDef();

        window.addEventListener("scroll", this.handleWindowScrollEvent, false);
    }

    componentDidMount() {
        document.addEventListener("keydown", this.handleOnKeyDown);
        document.addEventListener("keyup", this.handleOnKeyUp);
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.handleWindowScrollEvent, false);
        document.removeEventListener("keydown", this.handleOnKeyDown);
        document.removeEventListener("keyup", this.handleOnKeyUp);
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.sortingIndex !== this.props.sortingIndex ||
            prevProps.view !== this.props.view ||
            prevState.viewDef !== this.state.viewDef ||
            prevProps.dragging !== this.props.dragging ||
            this.props.isFetching !== prevProps.isFetching
        ) {
            this._grid.recomputeGridSize(); // row heights could have changed depending on label and item order
            this.handleWindowScrollEvent();
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // switching brands resets grid viewDef
        if (
            (nextProps?.scope?.brand && this.props?.scope?.brand?._id !== nextProps.scope.brand._id) ||
            nextProps.viewDef.result !== this.props.viewDef.result
        ) {
            this.loadViewDef(nextProps);
        }

        if (!_.isEmpty(nextProps.results) || !_.isEmpty(nextProps.entities.items)) {
            this.collectCatalogItems(nextProps);
        }

        const View = getViewBase(nextProps.view, nextProps.editable);

        // see if the grid needs updating
        if (
            nextProps.width !== this.props.width ||
            this.props.isFetching !== nextProps.isFetching ||
            this.props.sortingIndex !== nextProps.sortingIndex ||
            this.props.activeList !== nextProps.activeList ||
            this.props.results !== nextProps.results ||
            this.props.entities.entries !== nextProps.entities.entries ||
            nextProps.view !== this.props.view ||
            nextProps.dragging !== this.props.dragging
        ) {
            this.computeGrid(nextProps, View);
        }
    }

    collectCatalogItems(props = this.props) {
        const { entities, results: recentItemIds } = props;

        if (props.catalog && recentItemIds.length) {
            const catalogItems = [];
            for (let i = 0; i < Math.min(recentItemIds.length, 10); i++) {
                const itemId = entities?.entries?.[recentItemIds?.[i]]?.item_id || recentItemIds?.[i];
                if (entities?.items?.[itemId]) {
                    catalogItems.push(entities.items[itemId]);
                }
            }
            gtm.emit(
                gtm.events.viewItemList({
                    items: gtm.formatItems(
                        catalogItems,
                        props?.scope,
                        props.catalog?.type,
                        props.catalog?.id,
                        props.catalog?.title
                    ),
                    item_list_id: props.catalog?.id,
                    item_list_name: props.catalog?.title,
                    send_to: gtm.groups.Brand,
                })
            );
            gtm.emit(
                gtm.events.pageView({
                    page_location: `${window.location.protocol}//${window.location.host}/list/${props.catalog?.title}`,
                    page_title: `list: ${props.catalog?.type} - ${props.catalog?.title}`,
                    send_to: gtm.groups.Brand,
                })
            );
        }
    }

    loadViewDef(props = this.props) {
        const { viewDef: viewDefIds, viewDefs, scope } = props;
        const viewDef = getViewDef({
            ids: viewDefIds.result,
            entries: viewDefs,
            schema: scope.brand.getSchema("product"),
        });
        if (viewDef) {
            this.setState({ viewDef });
        }
    }

    computeGrid(props, View) {
        const {
            isFetching,
            results,
            entities,
            type,
            sortingIndex,
            pageSize,
            width,
            editable,
            draggingEntryId,
            overrideStyles = {},
        } = props;
        const { gridWidth, viewHorizontalOffset, viewWidth } = overrideStyles;
        const newGridWidth = gridWidth !== undefined ? gridWidth : width;
        const newViewWidth = viewWidth !== undefined ? viewWidth : View.width;
        const newViewHorizontalOffset =
            viewHorizontalOffset !== undefined ? viewHorizontalOffset : View.horizontalOffset;
        const columns = Math.floor(newGridWidth / (newViewWidth + newViewHorizontalOffset)) || 1;

        const fetchingRows = isFetching ? Math.floor(pageSize / columns) : 0;
        const result = (type === "catalog" || editable ? getGridForCatalog : getGridForItems)({
            result: results,
            columns,
            entities,
            sortingIndex,
            width: newGridWidth,
            editable,
            View,
            draggingEntryId,
            fetchingRows,
        });

        this.setState({
            columns,
            grid: result.grid,
            gridHeight: result.height + fetchingRows * (View.height + View.verticalOffset),
            estimatedRows: result.grid.length,
        });
    }

    handleOnKeyDown = (e) => {
        const { bulkItems } = this.props;
        const { groupSelectStartValue } = this.state;
        const bulkItemsArray = Array.from(bulkItems ? bulkItems.list : []);
        const bulkItemsArrayLength = bulkItemsArray.length;

        if (!groupSelectStartValue && bulkItemsArrayLength > 0) {
            this.setState({ shiftKey: e.shiftKey, groupSelectStartValue: bulkItemsArray[bulkItemsArrayLength - 1] });
        } else {
            this.setState({ shiftKey: e.shiftKey });
        }
    };

    handleOnKeyUp = () => {
        this.setState({ shiftKey: false });
    };

    handleBulkItemSelectionChange = (e, checked) => {
        const { onAddToSelectedItems, onRemoveFromSelectedItems, bulkItems, results, onSnackbarAdd, entities } =
            this.props;
        const { shiftKey, groupSelectStartValue } = this.state;
        const { entries } = entities;
        const bulkItemsLength = bulkItems.size;
        const selectionStartValue = e.target.value;

        // We only want to do the ShiftKey work if we have at least one item in bulkSelect
        if (shiftKey && bulkItemsLength > 0) {
            const selectedIds = generateSelectedIdArray(groupSelectStartValue, selectionStartValue, results);
            const bulkItemsArray = Array.from(bulkItems.list);
            const uniqueItems = bulkItemsArray.concat(
                selectedIds.filter((item) => bulkItemsArray.indexOf(item.toString()) < 0)
            );
            const cleanItems = entries
                ? uniqueItems.filter((id) => !entries[id] || (entries[id] && entries[id].type === ITEM))
                : uniqueItems;

            if (cleanItems.length <= MAX_SELECT) {
                cleanItems.map((id) => onAddToSelectedItems(id));
            } else {
                // pop a toast imploring folks to select fewer than MAX_SELECT
                onSnackbarAdd({
                    message: Globalize.formatMessage("Please limit selection to no more than |MAX_SELECT| items", {
                        MAX_SELECT,
                    }),
                });
            }
        } else {
            if (checked) {
                onAddToSelectedItems(selectionStartValue);
            } else {
                onRemoveFromSelectedItems(selectionStartValue);
            }
        }

        this.setState({
            groupSelectStartValue: selectionStartValue,
        });
    };

    editableCellRenderer({ columnIndex, rowIndex, key, style }) {
        style = _.clone(style); // style object can be freezed
        const {
            catalog,
            viewNotes,
            activeList,
            bulkItems,
            onReorderEntries,
            editable,
            activeItemId,
            cart,
            isActiveList,
            activeBrand,
            onSnackbarAdd,
            onDrag,
            onDrop,
            scope,
            view,
            onEntriesRefresh,
            entities,
            bulkDragInsert,
            bulkDragReorder,
            results,
            app,
        } = this.props;
        const { viewDef, grid } = this.state;
        const View = getViewBase(view, editable);

        // Loading, show tombstones
        if (!grid[rowIndex]) {
            return (
                <View
                    style={style}
                    key={key}
                    activeBrand={activeBrand}
                    onSnackbarAdd={onSnackbarAdd}
                    scope={scope}
                    hideBulkActions={catalog?.isProcessing?.()}
                    touchDevice={app.touchDevice}
                />
            );
        }

        const node = grid[rowIndex][columnIndex];

        if (!node) {
            // dont render things to the right of the label
            return null;
        }

        style.width =
            node.entry && (node.entry.type === ITEM_SEPARATOR || node.entry.type === "image") ? "100%" : node.width;
        style.height = node.height;

        key = getNodeKey(node) ?? key;

        if (node.entry && node.entry.type === ITEM_SEPARATOR) {
            const nextRowNode = grid?.[rowIndex + 1]?.[0];
            return (
                <SortableSeparator
                    actions={this.itemActions}
                    key={key}
                    style={style}
                    index={node.index}
                    catalogEntry={node.entry}
                    editable={editable}
                    dnd="dropable"
                    onDragHover={onReorderEntries}
                    onBeginDrag={onDrag}
                    onEndDrag={onDrop}
                    onSnackbarAdd={onSnackbarAdd}
                    emptySection={_.get(nextRowNode, "entry.type") === ITEM_SEPARATOR || nextRowNode?.placeholder}
                />
            );
        } else if (node.entry && node.entry.type === "image") {
            return (
                <SortableImage
                    actions={this.itemActions}
                    catalogEntry={node.entry}
                    editable={editable}
                    index={node.index}
                    key={key}
                    iconIsSeparator={node.entry.id !== node.entry.sync_id}
                    onDragHover={onReorderEntries}
                    onBeginDrag={onDrag}
                    onEndDrag={onDrop}
                    onSnackbarAdd={onSnackbarAdd}
                    style={style}
                    scope={scope}
                />
            );
        } else {
            const catalogDates = getCatalogDates(catalog);

            return (
                <View
                    actions={this.itemActions}
                    activeBrand={activeBrand}
                    activeItemId={activeItemId}
                    activeList={activeList}
                    bulkItems={bulkItems}
                    cart={cart}
                    catalogDates={catalogDates}
                    catalogEntry={node.entry}
                    catalog={catalog}
                    editable={editable}
                    index={node.index}
                    isActiveList={isActiveList}
                    item={node.item}
                    key={key}
                    onBeginDrag={onDrag}
                    onDragHover={onReorderEntries}
                    onEndDrag={onDrop}
                    placeholder={node.placeholder}
                    style={style}
                    view={viewDef}
                    viewNotes={viewNotes}
                    fullWidth={view === "list"}
                    onSnackbarAdd={onSnackbarAdd}
                    scope={scope}
                    hideBulkActions={catalog?.isProcessing?.()}
                    onEntriesRefresh={onEntriesRefresh}
                    schemas={entities.schemas}
                    bulkDragInsert={bulkDragInsert}
                    bulkDragReorder={bulkDragReorder}
                    results={results}
                    touchDevice={app.touchDevice}
                    viewport={app.viewport}
                />
            );
        }
    }

    cellRenderer(props) {
        const { columnIndex, rowIndex, key: gridKey } = props;
        const style = _.clone(props.style); // style object can be freezed
        const {
            viewNotes,
            bulkItems,
            activeList,
            activeItemId,
            catalog,
            cart,
            editable,
            isActiveList,
            app,
            onSnackbarAdd,
            view,
            scope,
            onEntriesRefresh,
            entities,
        } = this.props;
        const { viewDef, grid } = this.state;
        const View = getViewBase(view, editable);

        // Loading, show tombstones
        if (!grid[rowIndex]) {
            return (
                <View
                    style={style}
                    key={gridKey}
                    onSnackbarAdd={onSnackbarAdd}
                    scope={scope}
                    hideBulkActions={catalog?.isProcessing?.()}
                    touchDevice={app.touchDevice}
                />
            );
        }
        const node = grid[rowIndex][columnIndex];

        if (!node) {
            // dont render things to the right of the label
            return null;
        }

        const key = getNodeKey(node) ?? gridKey;

        style.width =
            node.entry && (node.entry.type === ITEM_SEPARATOR || node.entry.type === "image") ? "100%" : node.width;
        style.height = node.height;
        if (node.entry && node.entry.type === ITEM_SEPARATOR) {
            const nextRowNode = grid?.[rowIndex + 1]?.[0];
            return (
                <Separator
                    key={key}
                    style={style}
                    index={node.index}
                    width={node.width}
                    catalogEntry={node.entry}
                    onSnackbarAdd={onSnackbarAdd}
                    emptySection={_.get(nextRowNode, "entry.type") === ITEM_SEPARATOR || nextRowNode?.placeholder}
                />
            );
        } else if (node.entry && node.entry.type === "image") {
            return (
                <Image
                    catalogEntry={node.entry}
                    key={key}
                    onSnackbarAdd={onSnackbarAdd}
                    style={style}
                    width={node.width}
                    scope={scope}
                />
            );
        } else {
            const catalogDates = getCatalogDates(catalog);

            return (
                <View
                    actions={this.itemActions}
                    activeItemId={activeItemId}
                    activeList={activeList}
                    bulkItems={bulkItems}
                    catalogDates={catalogDates}
                    cart={cart}
                    catalogEntry={node.entry}
                    catalog={catalog}
                    editable={editable}
                    isActiveList={isActiveList}
                    item={node.item}
                    key={key}
                    style={style}
                    view={viewDef}
                    viewNotes={viewNotes}
                    onSnackbarAdd={onSnackbarAdd}
                    scope={scope}
                    hideBulkActions={catalog?.isProcessing?.()}
                    onEntriesRefresh={onEntriesRefresh}
                    schemas={entities.schemas}
                    touchDevice={app.touchDevice}
                />
            );
        }
    }

    handleWindowScrollEvent() {
        const { type, editable, hasMoreResults, isFetching, scope, ldFlags } = this.props;

        //Get windows scroll height based on nu57365LinesheetScrollHeightMultiplier to keep pre-fetching entries if improve linesheets pref is ON
        const heightMultiplier = ldFlags?.nu57365LinesheetScrollHeightMultiplier ?? SCROLL_HEIGHT_MULTIPLIER;
        const scrollHeight =
            type === "catalog" && scope?.brand?.hasPreference("improve_linesheets_search_performance")
                ? window.innerHeight * heightMultiplier
                : 0;

        // handle case when content is rendered by getGridForCatalog
        const preloadSectionHeight =
            type === "catalog" || editable
                ? rawTheme.preloadSectionHeight.catalog
                : rawTheme.preloadSectionHeight.items;
        if (
            window.pageYOffset + window.innerHeight + preloadSectionHeight + scrollHeight >
                document.documentElement.scrollHeight &&
            hasMoreResults &&
            !isFetching
        ) {
            this.props.onFetchNextPage();
        }
    }

    getScrollOffset(scrollTop) {
        const { editMode, catalog } = this.props;

        if (editMode) {
            const editModeOffset = SCROLL_WINDOW_EXTENSION[catalog.type];
            return scrollTop - editModeOffset < 0 ? 0 : scrollTop - editModeOffset;
        }
        return scrollTop;
    }

    render() {
        const { editable, view, scrollTop, width, height, overrideStyles = {} } = this.props;
        const { app, type, scope, onSnackbarAdd, results, isFetching, toolbarMinimized } = this.props;
        const { columns, grid, estimatedRows } = this.state;
        const View = getViewBase(view, editable);

        const { gridWidth, viewHorizontalOffset, viewWidth } = overrideStyles;
        const newViewWidth = viewWidth !== undefined ? viewWidth : View.width;
        const newViewHorizontalOffset =
            viewHorizontalOffset !== undefined ? viewHorizontalOffset : View.horizontalOffset;

        return (
            <div {...this.styles} className="gridContainer">
                {editable && app.touchDevice && (
                    <DragPreview
                        view={view}
                        activeBrand={scope?.brand}
                        onSnackbarAdd={onSnackbarAdd}
                        type={type}
                        scope={scope}
                    />
                )}
                <Grid
                    ref={(ref) => (this._grid = ref)}
                    columnWidth={estimatedRows ? newViewWidth + newViewHorizontalOffset : gridWidth || width}
                    columnCount={columns}
                    noContentRenderer={this.props.noContentRenderer}
                    cellRenderer={editable ? this.editableCellRenderer : this.cellRenderer}
                    rowHeight={({ index }) => (grid[index] ? grid[index][0].height : View.height + View.verticalOffset)}
                    rowCount={results.length === 0 && !isFetching && toolbarMinimized ? 0 : estimatedRows}
                    height={height || View.height + View.verticalOffset}
                    width={gridWidth || width}
                    scrollTop={this.getScrollOffset(scrollTop)}
                    autoHeight={true}
                />
            </div>
        );
    }
}

// prettier-ignore
const GridHOC = (props) => {
    const rootElement = useMemo(
        () => props.app?.touchDevice ? document.getElementById("main-content") : window,
        [props.app?.touchDevice],
    );

    if (!props.activeBrand) {
        return null;
    }

    return (
        <WindowScroller scrollElement={rootElement}>
            {({ scrollTop, height }) => (
                <AutoSizer disableHeight={true}>
                    {({ width }) => (
                        <ItemsGrid scrollTop={scrollTop} width={width} height={height} {...props} />
                    )}
                </AutoSizer>
            )}
        </WindowScroller>
    );
};

export default GridHOC;

export function getCatalogDates(catalog) {
    let catalogDates = EMPTY_CATALOG_DATES;
    if (catalog && catalog.delivery_window_start && catalog.delivery_window_end) {
        catalogDates = {
            ship_start: catalog.delivery_window_start,
            ship_end: catalog.delivery_window_end,
            catalogPrebook: catalog.prebook,
        };
    }
    return catalogDates;
}
